// The world's route bot (gate C1): the hero walks the layout's route (the path marked `route`) from the start to its
// end at walking speed (the stick at the walk tilt), steering leg by leg along the path's points (split every few
// metres), while both streaming tiers run: the sim tier on every step, the view tier on every drawn frame. Its report
// is the route's walking time and the streamer's numbers (late chunks on either tier, steps with a character over an
// unloaded collider, layers out of step, nav tile builds). Resumable, so a spec drives it in slices of frames.
import { createRouteBot, type RouteBot, type RouteLeg } from "@rpg/harness";
import { forwardOf, rightOf } from "@rpg/controller";
import type { Runtime } from "@rpg/runtime";
import { pointAlong, polyline } from "@rpg/world";
import type { WorldMount } from "./scene";
import type { WorldView } from "./view";
import { teleportHero } from "./teleport";

/** The route bot's report. */
export interface WorldRouteReport {
  done: boolean;
  /** Every leg reached. */
  ok: boolean;
  legs: number;
  failed: string[];
  /** The route's length along its points (m), and the hero's walk along it. */
  length: number;
  simSeconds: number;
  minutes: number;
  meanSpeed: number;
  frames: number;
  sim: ReturnType<ReturnType<WorldMount["sim"]>["checks"]> & { residency: ReturnType<ReturnType<WorldMount["sim"]>["residency"]["stats"]> };
  view: { late: number; loads: number; unloads: number; lateLog: unknown; slices: unknown; drawn: ReturnType<WorldView["drawn"]> } | null;
}

interface Run {
  frameMs: number;
  draw: boolean;
  t0: number;
  frames: number;
  bot: RouteBot<RouteLeg>;
  legs: RouteLeg[];
  length: number;
  done: boolean;
}

/** The route as legs: the route path's points split so no leg is longer than `step` m. */
export function routeLegs(m: WorldMount, step = 5): { legs: RouteLeg[]; length: number } {
  const route = m.world.layout.paths.find((p) => p.route);
  if (!route) throw new Error("world: the layout has no route");
  const line = polyline(route.points);
  const n = Math.ceil(line.length / step);
  const legs: RouteLeg[] = [];
  for (let k = 1; k <= n; k++) {
    const [x, z] = pointAlong(line, (k / n) * line.length);
    legs.push({ name: `road ${k}/${n}`, to: { x, z }, timeout: 20, radius: k === n ? 0.8 : 1.6 });
  }
  return { legs, length: line.length };
}

/** The route bot on a world scene. */
export function worldRouteBot(m: WorldMount, runtime: Runtime, view: () => WorldView | null) {
  let run: Run | null = null;
  const report = (): WorldRouteReport => {
    if (!run) throw new Error("world route: not started");
    const results = run.bot.results;
    // the walking time: the legs' own times (the route's end lands inside a frame)
    const simSeconds = run.done ? results.reduce((a, r) => a + r.time, 0) : runtime.clock.time - run.t0;
    const v = view();
    const vs = v ? (v.streamer.stats() as Record<string, unknown>) : null;
    const s = m.sim();
    return {
      done: run.done,
      ok: run.done && results.length === run.legs.length && results.every((r) => r.ok),
      legs: results.length,
      failed: results.filter((r) => !r.ok).map((r) => r.name),
      length: run.length,
      simSeconds,
      minutes: simSeconds / 60,
      meanSpeed: run.length / Math.max(1e-9, simSeconds),
      frames: run.frames,
      sim: { ...s.checks(), residency: s.residency.stats() },
      view: vs && v ? { late: vs.late as number, loads: vs.loads as number, unloads: vs.unloads as number, lateLog: vs.lateLog, slices: vs.slices, drawn: v.drawn() } : null,
    };
  };
  return {
    /** Warp the hero to the route's start, clear the counts, and begin. `frameMs`: game ms per drawn frame. */
    start(o: { frameMs?: number; draw?: boolean; tilt?: number } = {}): void {
      const rig = m.rig();
      const { legs, length } = routeLegs(m);
      const route = m.world.layout.paths.find((p) => p.route)!;
      const [sx, sz] = route.points[0]!;
      teleportHero(m, sx, sz);
      const bot = createRouteBot(legs, () => rig.hero().feet);
      const tilt = o.tilt ?? rig.tuning.hero.walkTilt;
      // steer: the stick toward the leg's target at the walk tilt, in the camera's (held) yaw
      rig.drive = () => {
        const s = bot.steer();
        if (!s) {
          rig.actions.setStick("move", { x: 0, y: 0 });
          return;
        }
        const yaw = rig.control().cameraYaw;
        const f = forwardOf(yaw);
        const r = rightOf(yaw);
        rig.actions.setStick("move", { x: (s.dir.x * r.x + s.dir.z * r.z) * tilt, y: (s.dir.x * f.x + s.dir.z * f.z) * tilt });
      };
      rig.observe = () => bot.update(rig.dt);
      // a step to settle the warp (the teleport is a cut on both tiers), then the counts start clean
      runtime.advance(rig.dt * 1000 + 0.01, { draw: o.draw !== false });
      m.sim().resetChecks();
      view()?.streamer.resetStats();
      view()?.resetDrawn();
      run = { frameMs: o.frameMs ?? 200, draw: o.draw !== false, t0: runtime.clock.time, frames: 0, bot, legs, length, done: false };
    },
    /** Run up to `frames` drawn frames (or until the route is walked). Returns whether it is done. */
    run(frames: number): boolean {
      if (!run) throw new Error("world route: not started");
      for (let i = 0; i < frames && !run.done; i++) {
        runtime.advance(run.frameMs, { draw: run.draw });
        run.frames++;
        if (run.bot.done) run.done = true;
      }
      if (run.done) {
        const rig = m.rig();
        rig.drive = null;
        rig.observe = null;
        rig.actions.setStick("move", null);
      }
      return run.done;
    },
    report,
    /** How many legs have been walked (a tour's segments read it). */
    legsDone: (): number => run?.bot.results.length ?? 0,
    /** Stop steering (a spec that walks part of the route, then drives the hero itself). */
    stop(): void {
      const rig = m.rig();
      rig.drive = null;
      rig.observe = null;
      rig.actions.setStick("move", null);
      if (run) run.done = true;
    },
  };
}
